import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { USE_CASES } from '../data';
import { useInView } from '../hooks';
import { Icon } from './Icon';
import { UseCaseVisual } from './UseCaseVisual';

export function UseCases() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ref, inView] = useInView<HTMLDivElement>(0.3);
  const u = USE_CASES[active];

  // Auto-advance when in view and not paused
  useEffect(() => {
    if (!inView || paused) return;
    const t = window.setInterval(() => {
      setActive((a) => (a + 1) % USE_CASES.length);
    }, 3600);
    return () => window.clearInterval(t);
  }, [inView, paused]);

  const pick = (i: number) => { setActive(i); setPaused(true); };

  return (
    <section className="py-24" id="use-cases">
      <div className="mx-auto w-full max-w-[1180px] px-7">
        <div className="reveal max-w-[680px] mx-auto mb-14 text-center">
          <span className="lp-eyebrow"><Icon name="layers" size={13} /> Use cases</span>
          <h2 className="text-[clamp(28px,3.6vw,42px)] font-bold tracking-[-0.03em] leading-[1.1] mt-4">Every missed moment, answered</h2>
          <p className="text-[clamp(16px,1.5vw,19px)] text-[var(--text-2)] leading-[1.6] mt-3.5">
            Pick a scenario to see the impact. Turn it on in one click and let AI agents do the calling.
          </p>
        </div>

        <div
          className="uc-explorer reveal"
          ref={ref}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="uc-list">
            {USE_CASES.map((c, i) => (
              <button
                key={c.title}
                className={`uc-item ${i === active ? 'active' : ''} ${paused ? 'paused' : ''}`}
                onClick={() => pick(i)}
              >
                <span className="uc-item-accent" style={{ background: c.tintFg }} />
                <span className="uc-item-icon" style={{ background: c.tintBg, color: c.tintFg }}>
                  <Icon name={c.icon} size={18} />
                </span>
                <span className="uc-item-text">
                  <span className="uc-item-title">{c.title}</span>
                  <span className="uc-item-tag">{c.tag}</span>
                </span>
                <span className="uc-item-metric" style={{ color: c.tintFg }}>{c.metric}</span>
                <span className="uc-item-timer">
                  <span className="uc-item-timer-fill" style={{ background: c.tintFg }} />
                </span>
              </button>
            ))}
          </div>

          <div className="uc-panel">
            <div className="uc-panel-bar">
              <span className="uc-step-count">
                <span style={{ color: u.tintFg }}>{String(active + 1).padStart(2, '0')}</span>
                <span className="uc-step-total"> / {String(USE_CASES.length).padStart(2, '0')}</span>
              </span>
              <span className="uc-tag-chip" style={{ background: u.tintBg, color: u.tintFg }}>
                <Icon name="check" size={12} /> {u.tag}
              </span>
            </div>

            <div className="uc-panel-head">
              <span className="uc-panel-icon" style={{ background: u.tintBg, color: u.tintFg }}>
                <Icon name={u.icon} size={22} />
              </span>
              <div>
                <div className="uc-panel-title">{u.title}</div>
                <div className="uc-panel-desc">{u.desc}</div>
              </div>
            </div>

            <div className="uc-panel-metric">
              <span className="uc-big" style={{ color: u.tintFg }}>{u.metric}</span>
              <span className="uc-big-label">{u.metricLabel}</span>
            </div>

            <UseCaseVisual u={u} key={active} />

            <div className="uc-panel-caption">
              <Icon name="info" size={13} color="var(--text-4)" />
              {u.caption}
            </div>

            <div className="uc-panel-foot">
              <div className="uc-dots">
                {USE_CASES.map((_, i) => (
                  <button
                    key={i}
                    className={`uc-dot ${i === active ? 'on' : ''}`}
                    style={i === active ? { background: u.tintFg } : undefined}
                    onClick={() => pick(i)}
                    aria-label={`Go to use case ${i + 1}`}
                  />
                ))}
              </div>
              <Link className="lp-btn lp-btn-dark" style={{ padding: '10px 18px', fontSize: 14, borderRadius: 10 }} to="/app">
                Enable this scenario <Icon name="arrow-right" size={15} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
